/**
 * ForgotPasswordV2 — v2 reskin of auth/ForgotPassword (UI-v2 Phase 1e).
 *
 * Logic verbatim from v1. Presentation: AuthShellV2 + shadcn primitives + FormFieldV2.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useRequestPasswordReset } from "@/hooks/queries/auth/useRequestPasswordReset";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormFieldV2 } from "@/components/v2/patterns/FormV2";

export default function ForgotPasswordV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const requestReset = useRequestPasswordReset();

  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRateLimitError(null);

    if (!tenantSlug) return;

    requestReset.mutate(
      { tenantSlug, email },
      {
        onSuccess: () => {
          setSubmitted(true);
        },
        onError: (err) => {
          if (err.error === "rate_limited") {
            const retryAfter =
              typeof err.details["retry_after_seconds"] === "number"
                ? err.details["retry_after_seconds"]
                : null;
            setRateLimitError(
              `Too many requests. Try again${retryAfter ? ` in ${retryAfter} second${retryAfter === 1 ? "" : "s"}` : " later"}.`
            );
          } else {
            setSubmitted(true);
          }
        },
      }
    );
  };

  if (submitted) {
    return (
      <AuthShellV2
        title="Check your email"
        subtitle="If an account exists for that email, we sent a reset link."
      >
        <p className="text-center text-sm text-text-secondary">
          You can close this tab.
        </p>
      </AuthShellV2>
    );
  }

  return (
    <AuthShellV2
      title="Reset your password"
      subtitle="Enter your work email and we'll send a reset link."
      footer={
        <Link
          to={`${base}/login`}
          className="text-text-link underline hover:text-text-link-hover"
        >
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormFieldV2
          label="Email"
          htmlFor="email-v2"
          error={rateLimitError ?? undefined}
        >
          <Input
            id="email-v2"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-invalid={!!rateLimitError || undefined}
          />
        </FormFieldV2>
        <Button
          type="submit"
          className="w-full"
          disabled={requestReset.isPending}
          aria-busy={requestReset.isPending}
        >
          {requestReset.isPending ? "Sending…" : "Send reset link"}
        </Button>
      </form>
    </AuthShellV2>
  );
}
